// =============================================================================
// supplier-conditions-client.ts — Client API de la page « Informations générales sur le référencement »
// FOURNISSEUR (DBU §3.3, F2).
// =============================================================================
// Miroir HTTP de l'endpoint FOURNISSEUR `GET /supplier/conditions`
// (apps/api/src/rate-collection/supplier-conditions.controller.ts), session opaque sous
// withTenant(supplier_siret). LECTURE PURE : aucune mutation, aucune règle métier ici (autorité = API :
// siret de la session, agrégation périmètre / statut cycle / tri calculés serveur). AUCUNE donnée métier en
// localStorage (RULE_REF_006). Horodatages = ISO 8601 (string), jamais d'objet Date ORM.
//
// DTO = miroirs HTTP ASCII (parité DTO API supplier-conditions.dto.ts). La SPA ne couple PAS au paquet DB.
// Clients bénéficiaires : projetés SOUS le gate de diffusion admin (`campaign.clients_diffusion_enabled`,
// fail-closed) — noms résolus si diffusion ON, masqués (liste vide + cardinalité) si OFF.

/** Statut d'un cycle DÉRIVÉ serveur (§3.3) — renfort TEXTE, jamais une couleur seule (WCAG 2.1 AA). */
export type BriefingCycleStatusDto = 'AVenir' | 'EnCours' | 'Termine';

/** Jalon d'un cycle projeté au calendrier opérationnel (§3.3). */
export interface BriefingCycleDto {
  readonly cycleNumber: number;
  readonly openAt: string | null;
  readonly closeAt: string | null;
  readonly status: BriefingCycleStatusDto;
}

/** Un profil du périmètre métier (nomenclature §3.3) : code JeKo + libellé + descriptif court + volumétrie. */
export interface BriefingProfileDto {
  readonly profileCode: string;
  readonly profileLabel: string | null;
  readonly description: string | null;
  readonly cellCount: number;
}

/** Une famille de profils intégrée à la sollicitation (§3.3). `family` = libellé ou `null` (non renseignée). */
export interface BriefingFamilyDto {
  readonly family: string | null;
  readonly profiles: readonly BriefingProfileDto[];
  readonly profileCount: number;
  readonly cellCount: number;
}

/** Périmètre métier agrégé (§3.3) : familles + volumétrie totale. */
export interface BriefingPerimetreDto {
  readonly families: readonly BriefingFamilyDto[];
  readonly totalProfiles: number;
  readonly totalCells: number;
  readonly seniorityLevelCount: number;
}

/** Un client bénéficiaire DIFFUSÉ (grain ENTREPRISE) — SIREN + raison sociale, jamais de contact référent. */
export interface BriefingBeneficiaryDto {
  readonly siren: string;
  readonly raisonSociale: string;
}

/**
 * Section « Clients bénéficiaires » (§3.3 l.2608) — projection CONDITIONNELLE sous le gate de diffusion admin
 * (`campaign.clients_diffusion_enabled`, fail-closed). `diffusionEnabled=false` ⇒ `beneficiaries` VIDE (noms
 * masqués, seule la cardinalité `beneficiaryCount` est exposée) ; `diffusionEnabled=true` ⇒ liste des
 * bénéficiaires (raison sociale + SIREN). Autorité = API : l'UI relaie, ne réinterprète jamais le gate.
 */
export interface BriefingClientsDto {
  readonly diffusionEnabled: boolean;
  readonly beneficiaryCount: number;
  readonly beneficiaries: readonly BriefingBeneficiaryDto[];
}

/** Briefing §3.3 d'UNE campagne à laquelle le fournisseur en session est convié. */
export interface SupplierBriefingDto {
  readonly campaignId: string;
  readonly intitule: string;
  readonly reference: string;
  readonly campaignState: string;
  readonly cycles: readonly BriefingCycleDto[];
  readonly perimetre: BriefingPerimetreDto;
  readonly clients: BriefingClientsDto;
}

/** Réponse de la page §3.3 : briefings des campagnes du tenant (triées par échéance, la plus urgente en tête). */
export interface SupplierConditionsDto {
  readonly generatedAt: string;
  readonly campaigns: readonly SupplierBriefingDto[];
}

/** Genre d'erreur applicative de la page conditions, mappé pour un message UX explicite. */
export type SupplierConditionsApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'generic';

/**
 * Erreur applicative renvoyée par l'API conditions (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle ne
 * réinterprète JAMAIS la règle métier (autorité = API). Le `kind` permet un libellé UX ciblé.
 */
export class SupplierConditionsApiError extends Error {
  public readonly kind: SupplierConditionsApiErrorKind;

  public constructor(
    message: string,
    kind: SupplierConditionsApiErrorKind = 'generic',
  ) {
    super(message);
    this.name = 'SupplierConditionsApiError';
    this.kind = kind;
  }
}

/**
 * Contrat du client conditions FOURNISSEUR — miroir de l'endpoint §3.3. LECTURE PURE : le siret vient de la
 * session (jamais du body) ; l'agrégation/tri sont l'autorité de l'API. L'UI relaie l'affichage.
 */
export interface SupplierConditionsClient {
  /** GET page « Conditions de référencement » du fournisseur en session (briefings des campagnes). */
  getConditions(): Promise<SupplierConditionsDto>;
}
